import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { captureException } from '@sentry/browser'
import { useSetRecoilState } from 'recoil'
import { currentStatusProgressState, loaderState } from 'recoils/atoms'
import fileDownload from 'js-file-download'
import axios, { CancelTokenSource } from 'axios'

import { Skeleton, DownloadProgressModal } from 'components'
import { IconDownload } from 'components/Icons'
import {
  initTicketInfo,
  downloadTaskDetailPDF,
  FILENAME,
} from 'features/Ticket'
import { findTicketById, IResponseTicket } from '..'

import { setValue } from 'helpers/common'
import { useCancelToken } from 'helpers/hooks'
import renderTicketInfo from './renderTicketInfo'

import type { IResponseService } from 'types'
import type { TaskPDF, TicketDetail } from 'features/Ticket'
import type { ITicketInfoProps } from './model'

const TicketInfo: React.FC<ITicketInfoProps> = (props) => {
  const [ticketDetail, setTicketDetail] = useState<TicketDetail>(initTicketInfo)
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [percentCompleted, setPercentCompleted] = useState<number>(0)
  const [loadedSize, setLoadedSize] = useState<number>(0)
  const [totalSize, setTotalSize] = useState<number>(0)
  const [isOpenDownloadModal, setIsOpenDownloadModal] = useState<boolean>(false)

  const setTagStatus = useSetRecoilState(currentStatusProgressState)
  const setLoader = useSetRecoilState(loaderState)

  const { isCancel } = useCancelToken()
  const { t } = useTranslation(['common', 'ticket'])

  const {
    ticketId,
    setProductIds,
    setTicketDetail: setTicketDetailParent,
  } = props

  const base = 'f-ticket-info'
  const { id, code = '' } = ticketDetail
  const source = axios.CancelToken.source()

  const fetchTicketDetail = async (id: string) => {
    setIsLoadingDetail(true)

    try {
      const { data } = await findTicketById(id)
      const ticket = data as TicketDetail

      if (ticket) {
        setTicketDetail(ticket)
        setTicketDetailParent(data as IResponseTicket)
        setIsLoadingDetail(false)

        setTagStatus(ticket.statusProgress)

        const productIds = ticket.products.map((product) => product.productId)
        setProductIds(productIds)
        setIsError(false)
      }
    } catch (error) {
      captureException(error)

      setIsError(true)
    }
  }

  const downloadFile = async (code: string, source: CancelTokenSource) => {
    const filename = getFileName(code)

    try {
      setIsError(false)

      const response: IResponseService<TaskPDF> = await downloadTaskDetailPDF(
        id,
        {
          cancelToken: source.token,
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent

            setLoadedSize(Math.round(loaded / 1000))

            if (total) {
              setPercentCompleted(Math.round((loaded * 100) / total))
              setTotalSize(Math.round(total / 1000))
            }
          },
        },
      )

      const pdfFile = response.data as unknown

      if (pdfFile instanceof Blob) {
        fileDownload(pdfFile, filename)
        setPercentCompleted(100)
      }
    } catch (error) {
      if (isCancel(error)) return

      captureException(error)
      setIsError(true)
    }
  }

  const renderCode = (isLoading: boolean, code: string) => {
    return isLoading ? (
      <Skeleton.blackSkeleton height='18px' width='160px' />
    ) : (
      <div>
        <span className={`${base}__code-title`}>
          {t('ticket:ticketId.title')}
        </span>
        <span className={`${base}__code-value`}>{setValue(code)}</span>
      </div>
    )
  }

  const getFileName = (code: string) => {
    return `${FILENAME.TASK_DETAIL_PDF}-${code}.pdf`
  }

  const handleDownloadTaskDetailPDF = async () => {
    setIsOpenDownloadModal(true)
    setLoader(true)
    setPercentCompleted(0)
    setLoadedSize(0)
    setTotalSize(0)
    setIsError(false)
  }

  const renderExportPDF = (isLoading: boolean) => {
    return isLoading ? (
      <Skeleton.blackSkeleton height='18px' width='122px' />
    ) : (
      <div
        className={`${base}__export-pdf-layout`}
        onClick={handleDownloadTaskDetailPDF}
      >
        <IconDownload className='icon--white' />
        <span className={`${base}__export-pdf-text`}>
          {t('ticket:detail.export.pdf')}
        </span>
      </div>
    )
  }

  const handleCloseDownloadModal = () => {
    setIsOpenDownloadModal(false)
  }

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetail(ticketId)
    }
  }, [ticketId])

  useEffect(() => {
    if (isOpenDownloadModal) {
      downloadFile(code, source)
    }

    return () => {
      source.cancel()
    }
  }, [isOpenDownloadModal])

  return (
    <div className={`${base}`}>
      <div className={`${base}__code-layout`}>
        {renderCode(isLoadingDetail, code)}
        {renderExportPDF(isLoadingDetail)}
      </div>

      <div
        className={`${base}__card-content ${
          isLoadingDetail ? `${base}__card-content--loading` : ''
        }`}
        data-testid='task-info-content'
      >
        {renderTicketInfo(ticketDetail, isLoadingDetail)}
      </div>

      <DownloadProgressModal
        percent={percentCompleted}
        loadedSize={loadedSize}
        totalSize={totalSize}
        fileName={getFileName(code)}
        isOpen={isOpenDownloadModal}
        isError={isError}
        onClose={handleCloseDownloadModal}
      />
    </div>
  )
}

export default TicketInfo
